import { get } from 'lodash';
import { Form } from 'react-bootstrap';
import { Controller, FieldError, ValidationRule, useFormContext } from 'react-hook-form';
import { Primitive } from 'zod';

import ToggleControl from './ToggleControl';

type HookToggleFormControlProps<T extends Primitive, V extends Primitive> = {
    name: string;
    label?: string;
    rules?: Record<string, ValidationRule>;
    defaultValue: T | V;
    options: [{ value: T; label: string }, { value: V; label: string }];
    onChange?: (value: T | V) => void;
};

export default function HookToggleFormControl<T extends Primitive, V extends Primitive>(
    props: HookToggleFormControlProps<T, V>,
) {
    const { name, label, rules, onChange, ...rest } = props;
    const { control, formState, getValues } = useFormContext();

    return (
        <>
            {label && (
                <Form.Label>
                    {label}
                    {rules?.required && <span className="required-mark" />}
                </Form.Label>
            )}
            <Controller
                name={name}
                render={({ field }) => (
                    <ToggleControl
                        {...field}
                        {...rest}
                        onChange={(value) => {
                            field.onChange(value);
                            if (onChange) {
                                onChange(value as T | V);
                            }
                        }}
                        value={getValues(name)}
                    />
                )}
                control={control}
                rules={rules}
            />
            <Form.Control.Feedback type="invalid">
                {(get(formState.errors, name) as FieldError)?.message}
            </Form.Control.Feedback>
        </>
    );
}
